import { queryClient } from "@App";
import { type AccessScope, getAccessScopeId, isAccessRedirectError } from "@lib/access/access";
import { markAccessRedirectPending } from "@lib/access/redirectNotice";
import { queries } from "@lib/queries";
import isAuth from "@lib/utils/isAuth";
import { redirect } from "@tanstack/react-router";

export async function getUser() {
    if (!isAuth()) return null;

    try {
        const user = await queryClient.fetchQuery(queries.user.detail());
        return user;
    } catch (e) {
        if (isAccessRedirectError(e)) return null;
        throw e;
    }
}

export type AccessFallbackRoute = "/signin" | "/side-admin" | "/planning" | "/home";

export type AccessRedirectMessageKey =
    | "access_redirect_home"
    | "access_redirect_planning"
    | "access_redirect_selection"
    | "access_redirect_signin"
    | "access_redirect_unavailable_signin";

/** Scope-failure landing priority: account home, network home, insider page, then sign-in. */
export function getAccessFallbackRoute(
    user: Awaited<ReturnType<typeof getUser>>,
): AccessFallbackRoute {
    if (!user) return "/signin";
    if (getAccessScopeId("account")) return "/planning";
    if (getAccessScopeId("network")) return "/home";
    if (user.isInsider) return "/side-admin";
    return "/signin";
}

export function getAccessRedirectMessageKey(
    user: Awaited<ReturnType<typeof getUser>>,
    destination: AccessFallbackRoute,
): AccessRedirectMessageKey {
    switch (destination) {
        case "/home":
            return "access_redirect_home";
        case "/planning":
            return "access_redirect_planning";
        case "/side-admin":
            return "access_redirect_selection";
        case "/signin":
            return user || isAuth()
                ? "access_redirect_unavailable_signin"
                : "access_redirect_signin";
    }
}

export async function requireConnectedUser() {
    const user = await getUser();
    if (!user) {
        redirectToAccessFallback();
    }
}

export function redirectToAccessFallback(): never {
    markAccessRedirectPending();
    throw redirect({ to: "/" });
}

export function requireScope(scope: Exclude<AccessScope, "authenticated">): void {
    // A selected account changes the API context, so network-only views must return to account home.
    if (scope === "network" && getAccessScopeId("account")) {
        redirectToAccessFallback();
    }

    const scopeId = getAccessScopeId(scope);
    if (!scopeId) {
        redirectToAccessFallback();
    }
}

export const allowInsider = async () => {
    const user = await getUser();
    return user?.isInsider;
};
export const allowAdminAndUser = async () => {
    const user = await getUser();
    return user?.role === "admin" || user?.role === "user";
};
